import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';

/**
 * The one query client, and the one place query keys are spelled.
 *
 * A MODULE RATHER THAN A `useState` IN `main.tsx`, because two callers need it and
 * only one of them is a component. `lib/membership` is a store with a plain
 * `toggleJoined(space)` that components call without a hook — it was the seam a real
 * mutation replaces, and a mutation needs a client. Importing the same instance is
 * how it gets one without every call site growing a parameter.
 *
 * KEYS ARE WRITTEN ONCE HERE. A key is a contract between the thing that reads and
 * the thing that invalidates: `setLike` has to invalidate the exact array `getFeed`
 * was cached under, and two hand-spelled copies of `['feed']` agree until the day one
 * of them takes a parameter.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      /* A tab revisited after a conversation should paint from memory. The cache
         remains bounded by its query keys; freshness still decides when it is
         reconciled in the background. */
      gcTime: 30 * 60_000,
      retry: 1,
    },
  },
});

/**
 * Retry, unless the answer was "there is no such thing".
 *
 * A 404 IS AN ANSWER, NOT A FAILURE — a handle that names nobody, a room that is
 * not there, an invitation that does not resolve — so retrying it delays a screen
 * that already knows what to draw. Everything else gets one more go.
 *
 * HERE BECAUSE THREE SCREENS HAD IDENTICAL COPIES OF IT, each with its own comment
 * saying the same thing, and one of them had begun pointing at another as the
 * explanation. A query key and a query's retry rule are the same kind of shared
 * contract, so they live in the same file.
 */
export function retryUnlessMissing(count: number, error: unknown): boolean {
  return !(error instanceof ApiError && error.status === 404) && count < 1;
}

export const keys = {
  /** Who is asking. Read by the session provider and by nothing else. */
  me: ['me'] as const,
  /** Which sign-in methods exist. */
  providers: ['providers'] as const,
  /**
   * Whether one handle may be had, keyed by the candidate itself.
   *
   * THE CACHE IS THE RATE LIMIT. Keyed per string and never stale, so typing
   * `yasmin` forward and back asks the server six times rather than eleven, and
   * a second visit to the field asks nothing at all.
   */
  handle: (candidate: string) => ['handle', candidate] as const,
  feed: ['feed'] as const,
  /** The room directory plus which of them you are in. */
  spaces: ['spaces'] as const,
  /** The rooms you may post to — memberships only, which is a different list. */
  mySpaces: ['my-spaces'] as const,
  spaceSearch: (q: string) => ['space-search', q] as const,
  space: (slug: string) => ['space', slug] as const,
  /**
   * One open letter and the comments under it, keyed by the LETTER's id.
   *
   * Keyed by the letter rather than by whatever id was tapped, because the server
   * answers a comment's id with the conversation that comment is in (`GET
   * /posts/:id`) — two keys for one payload would let the letter's own screen and
   * the composer addressed to it hold different copies of the same thread, and only
   * one of them would be invalidated when a comment is sent.
   */
  letter: (id: string) => ['letter', id] as const,
  profile: (handle: string) => ['profile', handle] as const,
  /**
   * EVERY cached card at once, which is a prefix of the key above rather than a
   * second key (docs/5 § D260813-0012).
   *
   * IT EXISTS FOR ONE CASE AND SHOULD STAY RARE: withdrawing a comment changes
   * whether the LETTER it answered still draws its fold, and the card that has to
   * learn is the letter author's — who is not the person doing the withdrawing, and
   * whose handle the deleting surface does not always hold. A room's flat run of
   * writing does not carry `replyingTo` on a comment at all, so a precise
   * invalidation would work on the conversation and on a profile and silently do
   * nothing in a room. Reaching for the prefix is what makes it correct everywhere.
   *
   * Prefer `profile(handle)` for anything that knows whose card changed. This is
   * blunt on purpose, and the bluntness is the point: a fold that outlives its
   * conversation opens an empty screen, which is the one thing § D260813-0012
   * promises cannot happen.
   */
  profiles: ['profile'] as const,
  people: ['people'] as const,
  /**
   * Everybody an `@` may name. One key, no parameter, and that is the point —
   * the list is fetched whole and filtered on the device (docs/5 § D260809-0021),
   * so there is no per-query cache entry to accumulate and no request to make
   * while somebody types.
   */
  mentionable: ['mentionable'] as const,
  loft: ['loft'] as const,
  correspondence: (handle: string) => ['correspondence', handle] as const,
  notifications: ['notifications'] as const,
  unread: ['unread'] as const,
  /** Your own invite link. One per account and it never changes, so the sheet
   *  that shows it fetches once and holds it. */
  invite: ['invite'] as const,
  /** Who a `/join/:code` link is from. Keyed on the code because a person may
   *  hold two — a link from a friend, then a link from somebody else. */
  invitation: (code: string) => ['invitation', code] as const,
  /**
   * A page of GIFs, keyed by the search that produced it — and the empty string
   * is a real key, because "no query" is the popular set rather than no request
   * (docs/5 § D260805-1221).
   *
   * THE CACHE IS WHAT MAKES THE PICKER FEEL LIKE A PICKER. Typing `thank`,
   * backspacing to `than` and typing forward again asks the server once; and a
   * sheet reopened on the same search draws from memory rather than from a
   * network round trip, which is the difference between a grid that appears and
   * a grid that loads.
   */
  gifs: (q: string) => ['gifs', q] as const,
  /** The ones you starred. One list per account, invalidated by the star. */
  gifFavourites: ['gif-favourites'] as const,
  /**
   * The purse and the ledger, which are one payload (docs/5 § D260806-2033).
   *
   * ONE KEY FOR BOTH, because the server sends them together and for a reason:
   * the ledger is the only place a balance may be explained, so a cache that
   * could hold a fresh number beside a stale list is a cache that can render a
   * balance nothing accounts for.
   */
  crumbs: ['crumbs'] as const,
  /** Who may be given breadcrumbs, and which of them already have today's. */
  giftTargets: ['gift-targets'] as const,
};
